import { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { formatDateTime } from "../utils/datetime";

// Per-device alerting + maintenance controls (Wave 5). Lets a tech set the
// device role, mute/enable individual alert metrics for this device, and put
// the device into maintenance mode for a fixed duration (auto-expiring).

type Config = {
  device_id: number;
  role: string;
  effective: Record<string, boolean>;
  overrides: { metric: string; enabled: boolean; delay_seconds?: number | null }[];
  offline_delay_seconds: number;
};

type DeviceLite = {
  id: number;
  maintenance_mode: boolean;
  maintenance_until?: string | null;
  role?: string;
};

const METRIC_LABELS: Record<string, string> = {
  device_offline: "Device offline",
  disk_used_pct: "Disk usage",
  memory_used_pct: "Memory usage",
  cpu_used_pct: "CPU usage",
  battery_health_pct: "Battery health",
  cpu_temp_c: "CPU temperature",
  chassis_temp_c: "Chassis temperature",
  ups_health_pct: "UPS battery",
  disk_io_latency_ms: "Disk I/O latency",
  fan_health: "Fan health",
  ups_status: "UPS status",
  raid_health: "RAID health",
  psu_status: "PSU status",
  certificate_expiry: "Certificate expiry",
};

const ROLES = [
  { v: "auto", l: "Auto-detect" },
  { v: "server", l: "Server" },
  { v: "workstation", l: "Workstation" },
  { v: "unknown", l: "Unknown" },
];

export default function DeviceAlertingPanel({ deviceId }: { deviceId: number }) {
  const [cfg, setCfg] = useState<Config | null>(null);
  const [device, setDevice] = useState<DeviceLite | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [delayDraft, setDelayDraft] = useState("");

  const load = useCallback(() => {
    setErr("");
    api
      .get(`/api/v1/alerts/devices/${deviceId}/overrides`)
      .then((r) => setCfg(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
    api
      .get(`/api/v1/devices/${deviceId}`)
      .then((r) => setDevice(r.data))
      .catch(() => {});
  }, [deviceId]);
  useEffect(load, [load]);
  useEffect(() => {
    if (cfg) setDelayDraft(String(cfg.offline_delay_seconds));
  }, [cfg]);

  const hasOverride = (metric: string) => !!cfg?.overrides.find((o) => o.metric === metric);
  const overrideFor = (metric: string) => cfg?.overrides.find((o) => o.metric === metric);

  const setMetric = async (metric: string, enabled: boolean) => {
    setBusy(true);
    try {
      // Preserve any per-device timer (e.g. offline delay) when toggling enable.
      const body: Record<string, unknown> = { enabled };
      const ds = overrideFor(metric)?.delay_seconds;
      if (ds != null) body.delay_seconds = ds;
      await api.put(`/api/v1/alerts/devices/${deviceId}/overrides/${metric}`, body);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const setOfflineDelay = async (delaySeconds: number | null) => {
    setBusy(true);
    try {
      // Keep the current effective enable-state; only change the timer.
      const body: Record<string, unknown> = { enabled: cfg?.effective["device_offline"] ?? true };
      if (delaySeconds != null) body.delay_seconds = delaySeconds;
      await api.put(`/api/v1/alerts/devices/${deviceId}/overrides/device_offline`, body);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const resetMetric = async (metric: string) => {
    setBusy(true);
    try {
      await api.delete(`/api/v1/alerts/devices/${deviceId}/overrides/${metric}`);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const setRole = async (role: string) => {
    setBusy(true);
    try {
      await api.put(`/api/v1/devices/${deviceId}/role`, { role });
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const setMaintenance = async (days: number | null) => {
    setBusy(true);
    try {
      const until = days != null ? new Date(Date.now() + days * 86400_000).toISOString() : null;
      await api.post(`/api/v1/devices/${deviceId}/maintenance`, { enabled: true, until });
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const exitMaintenance = async () => {
    setBusy(true);
    try {
      await api.post(`/api/v1/devices/${deviceId}/maintenance`, { enabled: false });
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return <div className="p-6 text-sm text-gray-400">Loading…</div>;

  const metrics = Object.keys(cfg.effective);

  return (
    <div className="space-y-4">
      {err && (
        <div className="bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
          {err}
        </div>
      )}

      {/* Role */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Device role</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Drives type-aware defaults — e.g. servers alert on offline, workstations don't.
            </p>
          </div>
          <select
            disabled={busy}
            value={ROLES.some((r) => r.v === cfg.role) ? cfg.role : "unknown"}
            onChange={(e) => setRole(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm dark:bg-gray-900"
          >
            {ROLES.map((r) => (
              <option key={r.v} value={r.v}>
                {r.l}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Offline alert delay */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Offline alert delay
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Only alert once this device has been offline this long. Effective:{" "}
              <strong>{cfg.offline_delay_seconds}s</strong>{" "}
              {overrideFor("device_offline")?.delay_seconds != null
                ? "(per-device override)"
                : "(inherited from type/global)"}
              .
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={delayDraft}
              onChange={(e) => setDelayDraft(e.target.value)}
              className="w-28 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm dark:bg-gray-900"
            />
            <span className="text-xs text-gray-400">sec</span>
            <button
              onClick={() => setOfflineDelay(Number(delayDraft))}
              disabled={busy}
              className="text-xs px-3 py-1.5 rounded-lg bg-brand text-white disabled:opacity-50"
            >
              Set
            </button>
            {overrideFor("device_offline")?.delay_seconds != null && (
              <button
                onClick={() => setOfflineDelay(null)}
                disabled={busy}
                className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
              >
                inherit
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Maintenance mode */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Maintenance mode</h3>
        {device?.maintenance_mode ? (
          <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-sm text-amber-700 dark:text-amber-300">
              In maintenance — alerting muted
              {device.maintenance_until
                ? ` until ${formatDateTime(device.maintenance_until)}`
                : " until manually exited"}
              .
            </span>
            <button
              onClick={exitMaintenance}
              disabled={busy}
              className="text-xs px-3 py-1.5 rounded-lg bg-teal-600 text-white disabled:opacity-50"
            >
              Exit maintenance
            </button>
          </div>
        ) : (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Mute alerts for:</span>
            {[
              { l: "1 day", d: 1 },
              { l: "3 days", d: 3 },
              { l: "7 days", d: 7 },
              { l: "Until I exit", d: null as number | null },
            ].map((o) => (
              <button
                key={o.l}
                onClick={() => setMaintenance(o.d)}
                disabled={busy}
                className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                {o.l}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Per-metric toggles */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Alerts for this device
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Toggle which metrics alert on this device. Defaults come from the global / role policy;
            an explicit override shows a “reset” link.
          </p>
        </div>
        <table className="min-w-full text-sm">
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {metrics.map((m) => {
              const on = cfg.effective[m];
              return (
                <tr key={m}>
                  <td className="px-4 py-2">{METRIC_LABELS[m] || m}</td>
                  <td className="px-4 py-2 text-right">
                    {hasOverride(m) && (
                      <button
                        onClick={() => resetMetric(m)}
                        disabled={busy}
                        className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mr-3"
                      >
                        reset
                      </button>
                    )}
                    <button
                      onClick={() => setMetric(m, !on)}
                      disabled={busy}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        on ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600"
                      }`}
                      aria-pressed={on}
                      title={on ? "Alerting enabled" : "Alerting muted"}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          on ? "translate-x-4" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
